import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Archive } from "lucide-react";
import {
  OPPORTUNITY_STATUS_VALUES,
  OPPORTUNITY_STATUS_DISPLAY_NAMES,
  type OpportunityStageCategory,
  type OpportunityStageRequiredFields,
} from "@forgepoint/shared";
import {
  StageRequiredFieldsPicker,
  type OtherStageRequirements,
} from "./StageRequiredFieldsPicker";

/** Props for the SortableStageRow component. */
export interface SortableStageRowProps {
  id: string;
  name: string;
  description: string;
  probability: number | null;
  /** The analytics category this stage maps to. */
  category: OpportunityStageCategory;
  /** True for seeded default stages — cannot be archived. */
  isDefault: boolean;
  /** Whether the category can be changed (locked for seeded default stages). */
  categoryEditable: boolean;
  isDuplicate: boolean;
  disabled: boolean;
  requiredFields: OpportunityStageRequiredFields;
  customFieldNames: string[];
  otherStages: OtherStageRequirements[];
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onProbabilityChange: (probability: number | null) => void;
  onCategoryChange: (category: OpportunityStageCategory) => void;
  onRequiredFieldsChange: (
    requiredFields: OpportunityStageRequiredFields,
  ) => void;
  onArchive: () => void;
}

/**
 * A single draggable stage row within the OpportunitySettingsPanel.
 * Two-line layout: name + controls on top, description annotation below.
 * When the stage list is narrow, the category, probability and required fields
 * controls drop under the name on their own lines.
 * @param props - SortableStageRowProps
 * @returns The stage row
 */
export function SortableStageRow({
  id,
  name,
  description,
  probability,
  category,
  isDefault,
  categoryEditable,
  isDuplicate,
  disabled,
  requiredFields,
  customFieldNames,
  otherStages,
  onNameChange,
  onDescriptionChange,
  onProbabilityChange,
  onCategoryChange,
  onRequiredFieldsChange,
  onArchive,
}: SortableStageRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, opacity: isDragging ? 0.5 : 1 }}
      className="border-b border-border/50 last:border-b-0 py-2.5 px-1"
    >
      <div className="flex flex-wrap items-center gap-2">
        <div
          className={`shrink-0 ${disabled ? "cursor-default" : "cursor-grab active:cursor-grabbing"} touch-manipulation`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground/50" />
        </div>

        <Input
          placeholder="Stage name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={disabled}
          className={`flex-1 min-w-[140px] h-8 text-sm ${isDuplicate ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
        />

        {isDefault && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1.5 rounded-full border-primary/25 bg-primary/[0.04] px-2 py-0.5 text-[11px] font-medium text-primary/90"
            title="Default stage — can't be archived"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-primary/70" />
            Default
          </Badge>
        )}

        <div className="order-last flex basis-full items-center gap-2 pl-6 @[616px]/stages:order-none @[616px]/stages:basis-auto @[616px]/stages:pl-0">
          <Select
            value={category}
            onValueChange={(value) => {
              const next = OPPORTUNITY_STATUS_VALUES.find((v) => v === value);
              if (next) {
                onCategoryChange(next);
              }
            }}
            disabled={disabled || !categoryEditable}
          >
            <SelectTrigger
              aria-label="Category"
              className="h-8 w-[180px] text-sm"
              title={
                categoryEditable
                  ? "Changing the category updates the status of every opportunity in this stage"
                  : "A default stage's category can't be changed"
              }
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPPORTUNITY_STATUS_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {OPPORTUNITY_STATUS_DISPLAY_NAMES[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1 w-[120px]">
            <Input
              type="number"
              min={0}
              max={100}
              placeholder="-"
              value={probability ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                onProbabilityChange(
                  val === ""
                    ? null
                    : Math.min(100, Math.max(0, parseInt(val, 10) || 0)),
                );
              }}
              disabled={disabled}
              aria-label="Probability"
              className="h-8 text-sm w-20"
            />
            <span className="text-xs text-muted-foreground">%</span>
          </div>
        </div>

        <div className="order-last basis-full pl-6 @[860px]/stages:order-none @[860px]/stages:basis-auto @[860px]/stages:w-[180px] @[860px]/stages:pl-0">
          <StageRequiredFieldsPicker
            value={requiredFields}
            customFieldNames={customFieldNames}
            stageName={name.trim()}
            otherStages={otherStages}
            onChange={onRequiredFieldsChange}
            disabled={disabled}
          />
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={onArchive}
          disabled={disabled || isDefault}
          className="shrink-0 h-7 w-7 text-muted-foreground hover:text-destructive transition-colors"
          title={isDefault ? "Cannot archive a default stage" : "Archive stage"}
        >
          <Archive className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="pl-6 mt-1">
        <input
          placeholder="Describe when an opportunity belongs in this stage (used by the AI agent)"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={disabled}
          maxLength={500}
          className="w-full text-xs text-muted-foreground placeholder:text-muted-foreground/50 bg-transparent border-0 outline-none focus:ring-0 py-0.5 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
    </div>
  );
}
